import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Mail, KeyRound, ArrowRight, CheckCircle2, LogOut } from "lucide-react";

/**
 * One-time security setup shown after a member's first successful login (code-based).
 * Steps:
 *   1. Enter email + verify with 6-digit OTP (10-minute expiry)
 *   2. Set 4-digit login PIN (entered twice)
 *   3. Confirmation
 * From the second login onwards, users sign in with phone + PIN.
 */
export default function FirstLoginSetup() {
  const { user, refresh, logout } = useAuth();
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=pin, 4=done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const requestOtp = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return toast.error("Please enter a valid email address");
    setBusy(true);
    try {
      await api.post("/auth/request-email-otp", { email });
      toast.success("A 6-digit code has been sent to your email.");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send verification code");
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (code.length !== 6) return toast.error("Enter the 6-digit code from your email");
    setBusy(true);
    try {
      await api.post("/auth/verify-email-otp", { email, code });
      toast.success("Email verified");
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid or expired code");
    } finally { setBusy(false); }
  };

  const submitPin = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be exactly 4 digits");
    if (pin !== pinConfirm) return toast.error("PINs do not match");
    setBusy(true);
    try {
      await api.post("/auth/complete-setup", { email, code, pin, pin_confirm: pinConfirm });
      await refresh();
      setStep(4);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not complete setup";
      toast.error(msg);
      if (typeof msg === "string" && msg.toLowerCase().includes("code")) setStep(2);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#150733] noise-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src="/veloskin-logo.png" alt="Veloskin Agility Club" className="h-14 w-auto drop-shadow-[0_0_18px_rgba(240,21,79,0.35)]" />
            <div>
              <div className="text-white text-lg font-semibold tracking-tight">Veloskin Agility Club</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-[0.2em]">Secure your account</div>
            </div>
          </div>
          <button
            data-testid="setup-signout"
            onClick={logout}
            className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#170929] p-6 sm:p-8">
          {step === 1 && (
            <div className="mb-5 p-4 rounded-lg bg-pink-500/10 border border-pink-500/30 flex items-start gap-3" data-testid="setup-welcome">
              <CheckCircle2 className="w-5 h-5 text-pink-400 shrink-0 mt-0.5" strokeWidth={2} />
              <div className="text-sm text-pink-100">
                <span className="font-medium text-white">You're in!</span> Let's secure your account before you continue.
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 mb-6">
            <ShieldCheck className="w-5 h-5 text-pink-400 shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <div className="text-white font-medium">One-time security setup</div>
              <p className="text-sm text-slate-400 mt-1">
                To keep your account secure and enable PIN reset if needed, please complete this one-time setup.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-8">
            {[1,2,3].map((n) => (
              <div key={n} className={`flex-1 h-1 rounded-full ${step >= n ? "bg-pink-500" : "bg-white/10"}`} />
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-5" data-testid="setup-step-email">
              <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-wider">
                <Mail className="w-3.5 h-3.5" /> Step 1 · Email verification
              </div>
              <div className="text-sm text-slate-400">Hi {user?.name?.split(" ")[0] || "there"} — enter the email where we should send your verification code.</div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Email address</Label>
                <Input
                  data-testid="setup-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="velo-input mt-2 h-12"
                />
              </div>
              <Button
                data-testid="setup-send-otp"
                onClick={requestOtp}
                disabled={busy}
                className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium"
              >
                Send verification code <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="setup-step-otp">
              <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-wider">
                <Mail className="w-3.5 h-3.5" /> Step 1 · Enter code
              </div>
              <div className="text-sm text-slate-400">We've sent a 6-digit code to <span className="text-white">{email}</span>. It expires in 10 minutes.</div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">6-digit code</Label>
                <Input
                  data-testid="setup-otp"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="velo-input mt-2 h-14 text-center font-mono text-2xl tracking-[0.5em]"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5"
                >
                  Change email
                </Button>
                <Button
                  data-testid="setup-verify-otp"
                  onClick={verifyOtp}
                  disabled={busy || code.length !== 6}
                  className="flex-1 h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium"
                >
                  Verify email <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <button
                data-testid="setup-resend-otp"
                onClick={requestOtp}
                disabled={busy}
                className="text-xs text-pink-400 hover:text-pink-300 underline underline-offset-4"
              >
                Resend code
              </button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="setup-step-pin">
              <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-wider">
                <KeyRound className="w-3.5 h-3.5" /> Step 2 · Set a 4-digit login PIN
              </div>
              <p className="text-sm text-slate-400">This PIN will be used to sign in from next time. Choose 4 digits you'll remember.</p>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">New PIN</Label>
                <Input
                  data-testid="setup-pin"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••"
                  className="velo-input mt-2 h-14 text-center font-mono text-2xl tracking-[0.5em]"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Confirm PIN</Label>
                <Input
                  data-testid="setup-pin-confirm"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••"
                  className="velo-input mt-2 h-14 text-center font-mono text-2xl tracking-[0.5em]"
                />
              </div>
              <Button
                data-testid="setup-complete"
                onClick={submitPin}
                disabled={busy || pin.length !== 4 || pinConfirm.length !== 4}
                className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium"
              >
                Finish setup <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5 text-center py-4" data-testid="setup-step-done">
              <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" strokeWidth={2} />
              </div>
              <div className="text-xl text-white font-medium">Your account is now secured.</div>
              <p className="text-sm text-slate-400 max-w-sm mx-auto">
                Use your 4-digit PIN to log in from next time.
              </p>
              <Button
                data-testid="setup-continue"
                onClick={refresh}
                className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-black font-medium"
              >
                Continue to dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
